/**
 * Step 3: Parse
 *
 * Sends the file or URL to the backend for AI parsing.
 * Shows a loading spinner while parsing, auto-advances on success.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { recruitingAPI } from "../../services/recruitingApi";
import type { JobImportWizardState, ParsedJobData } from "./JobImportWizard";

interface Props {
  wizardState: JobImportWizardState;
  onNext: () => void;
  onBack: () => void;
  updateState: (updates: Partial<JobImportWizardState>) => void;
}

const Step3_Parse: React.FC<Props> = ({
  wizardState,
  onNext,
  onBack,
  updateState,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [parseStatus, setParseStatus] = useState<
    "parsing" | "success" | "error"
  >("parsing");

  const isUrl = wizardState.importMethod === "url";

  // Shared mutation callbacks for both upload and URL parsing
  const makeMutationCallbacks = (fallbackErrorMsg: string) => ({
    onSuccess: (
      response: Awaited<ReturnType<typeof recruitingAPI.uploadJobDocument>>,
    ) => {
      if (response.success) {
        setParseStatus("success");
        updateState({
          parsedData: response.data as unknown as ParsedJobData,
          parseError: null,
          rawText: response.rawText,
        });
        setTimeout(() => onNext(), 1500);
      } else {
        setParseStatus("error");
        updateState({
          parseError: response.error || "AI extraction failed",
          rawText: response.rawText,
          parsedData: null,
        });
      }
    },
    onError: (error: any) => {
      setParseStatus("error");
      updateState({
        parseError:
          error?.response?.data?.error || error?.message || fallbackErrorMsg,
        parsedData: null,
      });
    },
  });

  const uploadMutation = useMutation(
    (formData: FormData) => recruitingAPI.uploadJobDocument(formData),
    makeMutationCallbacks("Failed to parse document"),
  );

  const urlMutation = useMutation(
    (url: string) => recruitingAPI.parseJobFromUrl(url),
    makeMutationCallbacks("Failed to parse URL"),
  );

  const triggerParse = () => {
    if (isUrl && wizardState.url) {
      urlMutation.mutate(wizardState.url);
    } else if (!isUrl && wizardState.uploadedFile) {
      const formData = new FormData();
      formData.append("document", wizardState.uploadedFile);
      uploadMutation.mutate(formData);
    } else {
      // No input available - show error instead of infinite spinner
      setParseStatus("error");
      updateState({
        parseError: isUrl
          ? "No URL provided. Please go back and enter a URL."
          : "No file selected. Please go back and upload a document.",
        parsedData: null,
      });
    }
  };

  // Auto-trigger parsing on mount
  useEffect(() => {
    triggerParse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    setParseStatus("parsing");
    updateState({ parseError: null, parsedData: null });
    triggerParse();
  };

  if (parseStatus === "parsing") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
          gap: 3,
        }}
      >
        <CircularProgress size={64} />
        <Typography variant="h6">
          {isUrl
            ? t(
                "jobImport.step3.parsingUrl",
                "Fetching and analyzing job posting...",
              )
            : t(
                "jobImport.step3.parsingDocument",
                "Analyzing job description...",
              )}
        </Typography>
        <Typography color="text.secondary">
          {t(
            "jobImport.step3.parsingHint",
            "AI is extracting job fields. This may take a few seconds.",
          )}
        </Typography>
      </Box>
    );
  }

  if (parseStatus === "success") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
          gap: 2,
        }}
      >
        <Typography variant="h6" color="success.main">
          {t("jobImport.step3.success", "Job details extracted successfully!")}
        </Typography>
        <Typography color="text.secondary">
          {t("jobImport.step3.successHint", "Redirecting to review form...")}
        </Typography>
      </Box>
    );
  }

  // Error state
  return (
    <Box>
      <Alert severity="error" sx={{ mb: 3 }}>
        {wizardState.parseError ||
          t("jobImport.step3.errorGeneric", "Failed to extract job details.")}
      </Alert>

      {wizardState.rawText && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t("jobImport.step3.rawTextPreview", "Extracted text preview:")}
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: "grey.50",
              borderRadius: 1,
              maxHeight: 200,
              overflow: "auto",
              fontFamily: "monospace",
              fontSize: "0.85rem",
              whiteSpace: "pre-wrap",
            }}
          >
            {wizardState.rawText}
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <Button onClick={onBack} variant="outlined">
          {t("common:actions.back", "Back")}
        </Button>
        <Button variant="contained" onClick={handleRetry}>
          {t("jobImport.step3.retry", "Try Again")}
        </Button>
      </Box>
    </Box>
  );
};

export default Step3_Parse;
